import { useMemo, useRef } from "react";
import { useGLTF } from "@react-three/drei";
import type * as THREE from "three";
import { useDisposeSceneMaterials } from "@/entities/muscle/ui/anatomy-model.hooks";
import type { MuscleId } from "@/entities/muscle/model/muscles";
import type { MuscleHighlightMap } from "@/entities/exercise/model/exercise-types";
import type { ExerciseId } from "@/shared/domain/ids";
import type { LimbPivots } from "@/entities/muscle/ui/limb-rigging.utils";
import { AnatomyScene } from "@/entities/muscle/ui/anatomy-scene.ui";
import { AnatomyRig } from "@/entities/muscle/ui/anatomy-rig.ui";
import { findExercise } from "@/entities/exercise/model/exercises";
import { inferExerciseHeads } from "@/entities/muscle/ui/exercise-head-heuristics";

/**
 * AnatomyModel — loads a Z-Anatomy GLB, highlights muscles by name, and
 * animates limbs by reparenting meshes under joint pivots.
 */

type AnatomyModelProps = Readonly<{
  active: MuscleHighlightMap;
  exerciseId: ExerciseId;
  animate?: boolean;
  url?: string;
  onActiveBox?: (box: THREE.Box3 | null) => void;
  /** When provided, renders an HTML label at the center of each muscle. */
  muscleLabels?: Partial<Record<MuscleId, string>>;
}>;

const DEFAULT_URL = "/zanatomy_muscular.glb";

export const AnatomyModel = ({
  active,
  exerciseId,
  animate = true,
  url = DEFAULT_URL,
  onActiveBox,
  muscleLabels,
}: AnatomyModelProps) => {
  const { scene } = useGLTF(url);
  // Clone once per loaded GLB, not per render. Without a stable identity, every
  // prop change (e.g. selecting another exercise) produces a fresh clone, swaps
  // the whole <primitive>, and flashes the GLB's original (white) materials for
  // a frame before the highlight effects recolor it. The compiler can't memoize
  // an opaque .clone() call, so pin it explicitly.
  const clonedScene = useMemo(() => scene.clone(true), [scene]);
  const pivotsRef = useRef<LimbPivots>({});
  const shouldUseClassifier = /\b(anatomy|zanatomy)/i.test(url);
  // Heuristic: which specific heads this exercise biases (biceps long/short…).
  const activeHeads = useMemo(
    () => new Set(inferExerciseHeads(findExercise(exerciseId)?.name ?? "")),
    [exerciseId],
  );

  useDisposeSceneMaterials(clonedScene);

  return (
    <AnatomyRig
      clonedScene={clonedScene}
      exerciseId={exerciseId}
      animate={animate}
      pivotsRef={pivotsRef}
    >
      <AnatomyScene
        clonedScene={clonedScene}
        active={active}
        activeHeads={activeHeads}
        useClassifier={shouldUseClassifier}
        pivotsRef={pivotsRef}
        onActiveBox={onActiveBox}
        muscleLabels={muscleLabels}
      />
    </AnatomyRig>
  );
};

AnatomyModel.preload = (url: string = DEFAULT_URL): void => {
  useGLTF.preload(url);
};
